<script setup lang="ts">
import { useDesignLinks } from '@/components/calculation-form/design-link/composable/useDesignLinks'
import QuantityImportSummaryDialog from '@/components/calculation-form/design-link/QuantityImportSummaryDialog.vue'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'
import { ISSUE_SEVERITIES } from '@/constants/issueSeverity'

const props = defineProps<CalculationFormMixinProps>()
const {
  isReadOnly,
  isImporting,
  importedRowCount,
  showImportSummary,
  quantityBuildingBlockItems,
  buildingBlockItems,
  selectedBlockIdSet,
  fileInput,
  labelMenuQuantityId,
  labelDraft,
  labelSuggestions,
  buildingBlockUnit,
  hasCurrentUnitMismatch,
  hasMissingBuildingBlockUnit,
  hasUnacceptedUnitMismatch,
  isUnitMismatchAccepted,
  designLinkMissingMessages,
  designLinkChangedMessages,
  designLinkNoticeTitle,
  getImportIssueForQuantity,
  resolveDesignLinkImportIssueClass,
  resolveDesignLinkImportIssueChipColor,
  handleImportFile,
  onFileSelected,
  updateBuildingBlock,
  confirmQuantityRow,
  deleteQuantityRow,
  openLabelMenu,
  closeLabelMenu,
  setLabelDraft,
  saveQuantityLabels,
  removeQuantityLabel,
  localAlias,
  onAliasInput
} = useDesignLinks(props)
</script>
<template>
  <v-dialog :model-value="isImporting" persistent max-width="320">
    <v-card class="text-center py-7 px-8">
      <div class="d-flex justify-center mb-4">
        <v-progress-circular indeterminate color="primary" size="56" width="5" />
      </div>
      <div class="text-body-1">Importing quantities…</div>
    </v-card>
  </v-dialog>

  <v-container fluid class="pa-6">
    <!-- Header ----------------------------------------------------------- -->
    <v-row class="mb-4 align-center">
      <v-col cols="12" class="d-flex align-center ma-0">
        <h1 class="text-h4 mr-4">Design links</h1>

        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,.xls"
          style="display: none"
          @change="onFileSelected"
        />

        <v-btn
          prepend-icon="mdi-upload"
          variant="flat"
          rounded="pill"
          :disabled="isReadOnly"
          @click="handleImportFile"
        >
          Import Quantities
        </v-btn>
      </v-col>

      <v-col cols="12">
        <div class="text-body-2 text-medium-emphasis">
          <div>
            Link selected building blocks to calculation-specific quantities. Upload quantities
            using <b>Import Quantities</b>. Ensure your Excel file follows the format described in
            the SIM documentation to avoid errors.
          </div>
          <div>
            Each row in the quantity file represents a separate quantity that can be linked to one
            building block. The system multiplies the quantity by the building block value, so
            verify that units match. Adjust either the input file or the building block if
            necessary.
          </div>
          <div>
            A building block can be linked to multiple quantities, but each quantity is associated
            with one building block.
          </div>
          <div>
            If a previously imported row is missing from a new upload, it remains here and must be
            resolved before running the calculation. Use the <b>trash icon</b> to remove it, or
            re-upload a quantities file that includes the missing row.
          </div>
        </div>
      </v-col>

      <v-col
        v-if="designLinkMissingMessages.length || designLinkChangedMessages.length"
        cols="12"
        class="pt-0"
      >
        <v-alert type="warning" variant="tonal" density="comfortable" class="mb-0">
          <div class="text-body-2">
            {{ designLinkNoticeTitle }}
          </div>
          <div
            v-for="(message, index) in designLinkMissingMessages"
            :key="`design-link-missing-${index}`"
            class="text-body-2 mt-1"
          >
            {{ message }}
          </div>
          <div
            v-for="(message, index) in designLinkChangedMessages"
            :key="`design-link-changed-${index}`"
            class="text-body-2 mt-1"
          >
            {{ message }}
          </div>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Column headers --------------------------------------------------- -->
    <v-row class="mb-2">
      <v-col cols="12" md="6">
        <span class="text-body-2 font-weight-medium">Quantities</span>
      </v-col>
      <v-col cols="12" md="6">
        <span class="text-body-2 font-weight-medium">Building blocks</span>
      </v-col>
    </v-row>

    <!-- Rows ------------------------------------------------------------- -->
    <v-row>
      <v-col cols="12" v-for="quantity in quantityBuildingBlockItems" :key="quantity.id">
        <v-sheet
          class="design-row"
          :class="{
            'design-row--mismatch': hasUnacceptedUnitMismatch(quantity),
            'design-row--missing-unit': hasMissingBuildingBlockUnit(quantity),
            [resolveDesignLinkImportIssueClass(getImportIssueForQuantity(quantity))]:
              !!getImportIssueForQuantity(quantity)
          }"
        >
          <v-row>
            <!-- Quantities column - display only -->
            <v-col cols="12" md="6" class="d-flex align-center">
              <div class="flex-grow-1">
                <div class="text-body-2">{{ quantity.name }}</div>
                <v-text-field
                  :model-value="localAlias[quantity.id] ?? quantity.alias ?? ''"
                  density="compact"
                  variant="plain"
                  placeholder="Display name (optional)"
                  hide-details
                  :readonly="
                    isReadOnly ||
                    hasMissingBuildingBlockUnit(quantity) ||
                    (!!getImportIssueForQuantity(quantity) &&
                      getImportIssueForQuantity(quantity)?.severity === ISSUE_SEVERITIES.MISSING)
                  "
                  class="alias-field mt-1"
                  @update:model-value="(aliasValue: string) => onAliasInput(quantity, aliasValue)"
                />
                <div v-if="quantity.labels?.length" class="design-labels mt-1">
                  <v-chip
                    v-for="label in quantity.labels"
                    :key="`${quantity.id}-${label}`"
                    size="x-small"
                    color="primary"
                    variant="tonal"
                    class="mr-1 mb-1"
                    :closable="
                      !isReadOnly &&
                      !hasMissingBuildingBlockUnit(quantity) &&
                      getImportIssueForQuantity(quantity)?.severity !== ISSUE_SEVERITIES.MISSING
                    "
                    @click:close="removeQuantityLabel(quantity, label)"
                  >
                    {{ label }}
                  </v-chip>
                </div>
                <div v-if="getImportIssueForQuantity(quantity)" class="design-import-details mt-1">
                  <v-chip
                    size="x-small"
                    :color="
                      resolveDesignLinkImportIssueChipColor(getImportIssueForQuantity(quantity))
                    "
                    variant="tonal"
                    class="mr-2 mb-1"
                  >
                    {{ getImportIssueForQuantity(quantity)?.title }}
                  </v-chip>
                  <div class="design-import-text">
                    {{ getImportIssueForQuantity(quantity)?.summary }}
                  </div>
                  <div
                    v-for="(fieldDiff, index) in getImportIssueForQuantity(quantity)?.fieldDiffs ??
                    []"
                    :key="`import-changed-field-${quantity.id}-${index}`"
                    class="design-import-subtext"
                  >
                    {{ fieldDiff }}
                  </div>
                </div>
              </div>
              <div class="text-body-2 font-weight-medium">{{ quantity.unit }}</div>
              <v-menu
                :model-value="labelMenuQuantityId === quantity.id"
                :close-on-content-click="false"
                location="bottom end"
                @update:model-value="
                  (opened) => (opened ? openLabelMenu(quantity) : closeLabelMenu())
                "
              >
                <template #activator="{ props: labelActivatorProps }">
                  <v-btn
                    v-bind="labelActivatorProps"
                    icon="mdi-label-multiple-outline"
                    variant="text"
                    size="small"
                    class="ml-2"
                    :disabled="
                      isReadOnly ||
                      hasMissingBuildingBlockUnit(quantity) ||
                      getImportIssueForQuantity(quantity)?.severity === ISSUE_SEVERITIES.MISSING
                    "
                    @click.stop="openLabelMenu(quantity)"
                  />
                </template>
                <v-card class="design-label-menu" min-width="320">
                  <v-card-title class="text-subtitle-2 pb-0">Labels</v-card-title>
                  <v-card-text class="pb-2">
                    <v-combobox
                      :model-value="labelDraft"
                      :items="labelSuggestions"
                      multiple
                      chips
                      closable-chips
                      clearable
                      hide-details="auto"
                      density="comfortable"
                      variant="outlined"
                      label="Add labels"
                      hint="Maximum 3 labels"
                      persistent-hint
                      :counter="3"
                      @update:model-value="setLabelDraft"
                    />
                  </v-card-text>
                  <v-card-actions class="pt-0">
                    <v-spacer />
                    <v-btn variant="text" @click="closeLabelMenu">Cancel</v-btn>
                    <v-btn color="primary" @click="saveQuantityLabels(quantity)">Save</v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-col>

            <!-- Building blocks column - searchable select -->
            <v-col cols="12" md="6" class="d-flex align-center">
              <v-row>
                <template
                  v-if="
                    getImportIssueForQuantity(quantity)?.severity === ISSUE_SEVERITIES.MISSING
                  "
                >
                  <v-col cols="12" md="10" class="d-flex align-center pa-0">
                    <div class="text-body-2 text-medium-emphasis design-import-text pl-1">
                      Delete this row or re-upload a quantities file that includes it to restore the
                      building block link.
                    </div>
                  </v-col>
                  <v-col cols="12" md="2" class="d-flex align-center justify-end pa-0">
                    <v-btn
                      icon="mdi-delete"
                      variant="text"
                      size="small"
                      :disabled="isReadOnly"
                      @click="deleteQuantityRow(quantity)"
                    />
                  </v-col>
                </template>
                <template v-else>
                  <v-col cols="12" md="9" class="d-flex align-center pa-0">
                    <v-autocomplete
                      color="primary"
                      :model-value="quantity.building_block"
                      :items="buildingBlockItems.filter((item) => selectedBlockIdSet.has(item.id))"
                      :readonly="isReadOnly"
                      item-title="name"
                      item-value="id"
                      class="flex-grow-1 pb-2"
                      placeholder="Select building block"
                      density="comfortable"
                      variant="underlined"
                      prepend-inner-icon="mdi-magnify"
                      clearable
                      hide-details
                      @update:model-value="(value) => updateBuildingBlock(quantity, value)"
                    >
                      <template #item="{ props, item }">
                        <v-list-item v-bind="props" :title="item.raw.name">
                          <template #prepend>
                            <v-icon size="small" class="mr-2">mdi-copyright</v-icon>
                          </template>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-spacer />
                  <v-col cols="12" md="1" class="d-flex align-center pa-0">
                    <div class="text-body-2 font-weight-medium ml-2">
                      {{ buildingBlockUnit(quantity.building_block) }}
                    </div>
                  </v-col>
                  <v-col cols="12" md="2" class="d-flex align-center justify-end pa-0">
                    <v-btn
                      :icon="isUnitMismatchAccepted(quantity) ? 'mdi-check-circle' : 'mdi-check'"
                      variant="text"
                      size="small"
                      :color="
                        isUnitMismatchAccepted(quantity)
                          ? 'success'
                          : hasCurrentUnitMismatch(quantity)
                            ? 'warning'
                            : undefined
                      "
                      :disabled="isReadOnly || !hasCurrentUnitMismatch(quantity)"
                      @click="confirmQuantityRow(quantity)"
                    />
                    <v-btn
                      icon="mdi-delete"
                      variant="text"
                      size="small"
                      :disabled="isReadOnly"
                      @click="deleteQuantityRow(quantity)"
                    />
                  </v-col>
                </template>
              </v-row>
            </v-col>

            <v-col v-if="hasUnacceptedUnitMismatch(quantity)" cols="12" class="pt-3">
              <div class="design-row-warning text-caption">
                The calculation result will be affected by this. Please change the unit of this
                building block or, if you want to continue, accept this mismatch in the unit.
              </div>
            </v-col>
            <v-col v-if="hasMissingBuildingBlockUnit(quantity)" cols="12" class="pt-3">
              <div class="design-row-error text-caption">
                The selected building block does not have a unit. Please provide a unit value for
                the chosen building block or change to another building block.
              </div>
            </v-col>
          </v-row>
        </v-sheet>
      </v-col>
    </v-row>

    <v-row v-if="!quantityBuildingBlockItems.length">
      <v-col cols="12">
        <div class="pa-6 text-center text-medium-emphasis">
          No quantities available. Import an Excel file to get started.
        </div>
      </v-col>
    </v-row>

    <QuantityImportSummaryDialog v-model="showImportSummary" :count="importedRowCount" />
  </v-container>
</template>

<style scoped>
.design-row {
  background-color: #fafafa;
  border: 1px solid #e0e0e0;
  padding: 16px 24px;
  border-radius: 8px;
}

.design-row + .design-row {
  margin-top: 12px;
}

.design-row--mismatch {
  background-color: #fff5f5;
  border-color: #ef9a9a;
}

.design-row--missing-unit {
  background-color: #fbe9e7;
  border-color: #e53935;
}

.design-row--import-missing {
  border-color: rgb(var(--v-theme-error));
  background-color: rgba(var(--v-theme-error), 0.08);
}

.design-row--import-changed {
  border-color: rgb(var(--v-theme-warning));
  background-color: rgba(var(--v-theme-warning), 0.12);
}

.design-row-warning {
  color: #b71c1c;
}

.design-row-error {
  color: #c62828;
  font-weight: 500;
}

.design-import-details {
  line-height: 1.3;
}

.design-import-text {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.78);
}

.design-import-subtext {
  font-size: 0.75rem;
  opacity: 0.9;
  margin-left: 8px;
}

.design-labels {
  line-height: 1.4;
}

.design-label-menu {
  border-radius: 8px;
}

.alias-field {
  font-size: 0.8rem;
  opacity: 0.8;
}
.alias-field :deep(.v-field__input) {
  padding-top: 0;
  min-height: 24px;
  font-size: 0.8rem;
}

.unit-label {
  font-size: 14px;
  font-weight: 500;
}
</style>
